import { useLocation, useSearchParams } from 'react-router-dom';
import { useWhoami } from '@cotera/client/app/stores/org';

/**
 * Active link types for the Coco sidebar.
 * Add new types here as we add more navigation sections (e.g. projects).
 */
export type CocoSidebarLinkType =
  | { type: 'new-chat' }
  | { type: 'chats' }
  /** Chat history scoped to a folder (`?folderId=`). */
  | { type: 'chats-folder'; folderId: string }
  /** Chat history for unfiled chats in Private or Shared (`?scope=&unfiled=1`). */
  | { type: 'chats-unfiled'; scope: 'private' | 'shared' }
  | { type: 'chat'; id: string }
  | { type: 'agents' }
  | { type: 'agent'; id: string }
  /** The Library and any of its tabs (folders, agents, documents, workflows). */
  | { type: 'library' }
  | { type: 'folders' }
  | { type: 'folder'; id: string }
  | { type: 'triggers' }
  | { type: 'trigger'; id: string }
  | { type: 'workflows' }
  | { type: 'workflow'; id: string }
  | { type: 'documents' }
  | { type: 'document'; id: string }
  | { type: 'datasets' }
  | { type: 'dataset'; id: string }
  | { type: 'project'; id: string };

type CocoSidebarLink = CocoSidebarLinkType;

/**
 * Returns whether the given link is currently active based on the URL.
 * Use this hook to derive active state from the URL bar for sidebar items.
 *
 * @example
 * const isActive = useCocoSidebarActive();
 * // NavMenu - New chat
 * <SidebarMenuButton isActive={isActive({ type: 'new-chat' })} />
 * // NavMenu - Chats list
 * <SidebarMenuButton isActive={isActive({ type: 'chats' })} />
 * // Recent chat item
 * <SidebarMenuButton isActive={isActive({ type: 'chat', id: conversation.id })} />
 */
export function useCocoSidebarActive(): (link: CocoSidebarLink) => boolean {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const org = useWhoami((s) => s.org);

  return (link: CocoSidebarLink): boolean => {
    const pathname = location.pathname.replace(/\/$/, '') || '/';
    const orgPrefix = `/${org.slug}`;

    switch (link.type) {
      case 'new-chat':
        return pathname === orgPrefix;
      case 'chats': {
        const chatsPath = `${orgPrefix}/chats`;
        if (pathname.startsWith(`${chatsPath}/`)) {
          return true;
        }
        if (pathname !== chatsPath) {
          return false;
        }
        const folderId = searchParams.get('folderId');
        const unfiled = searchParams.get('unfiled');
        const scope = searchParams.get('scope');
        if (folderId !== null && folderId !== '') {
          return false;
        }
        if (unfiled === '1' && (scope === 'private' || scope === 'shared')) {
          return false;
        }
        return true;
      }
      case 'chats-folder': {
        if (pathname !== `${orgPrefix}/chats`) {
          return false;
        }
        return searchParams.get('folderId') === link.folderId;
      }
      case 'chats-unfiled': {
        if (pathname !== `${orgPrefix}/chats`) {
          return false;
        }
        return (
          searchParams.get('unfiled') === '1' &&
          searchParams.get('scope') === link.scope
        );
      }
      case 'chat':
        return pathname === `${orgPrefix}/chats/${link.id}`;
      case 'library': {
        // Active on the Library and its resource routes. Workflows have their
        // own sidebar entry (and `workflows` active-type), so exclude them here.
        // Datasets are browsed from the Library like agents and documents, so
        // they highlight it too — without this a dataset page lit nothing at
        // all, leaving the reader no clue which surface they were under.
        const librarySegments = [
          '/folders',
          '/agents',
          '/agent',
          '/documents',
          '/datasets',
        ];
        return librarySegments.some(
          (seg) =>
            pathname === `${orgPrefix}${seg}` ||
            pathname.startsWith(`${orgPrefix}${seg}/`)
        );
      }
      case 'agents':
        return pathname === `${orgPrefix}/agents`;
      case 'agent':
        return pathname === `${orgPrefix}/agent/${link.id}`;
      case 'triggers':
        return pathname.startsWith(`${orgPrefix}/triggers`);
      case 'trigger':
        return pathname === `${orgPrefix}/triggers/${link.id}`;
      case 'workflows':
        return pathname.startsWith(`${orgPrefix}/workflows`);
      case 'workflow':
        return pathname === `${orgPrefix}/workflows/${link.id}`;
      case 'folders':
        return pathname === `${orgPrefix}/folders`;
      case 'folder':
        return pathname === `${orgPrefix}/folders/${link.id}`;
      case 'documents':
        return pathname === `${orgPrefix}/documents`;
      case 'document':
        return pathname === `${orgPrefix}/documents/${link.id}`;
      case 'datasets':
        return pathname.startsWith(`${orgPrefix}/datasets`);
      case 'dataset':
        return pathname === `${orgPrefix}/datasets/${link.id}`;
      case 'project':
        return pathname === `${orgPrefix}/projects/${link.id}`;
      default: {
        const _exhaustive: never = link;
        return false;
      }
    }
  };
}
